import type { ReactNode } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import type { IconDefinition } from '#src/atoms/icons/index.tsx';
import { FontAwesomeIcon } from '#src/atoms/icons/index.tsx';
import { iconFile } from '#src/atoms/icons/system/iconFile.ts';
import { BodyM, LabelM, LabelS } from '#src/atoms/typography/index.tsx';
import { UploadFile } from '#src/components/form/UploadFile/index.tsx';
import type { FileWithDataURL } from '#src/components/form/UploadFile/useUploadFile.ts';

type Props = {
  icon: IconDefinition;
  file: FileWithDataURL | undefined;
  name: string;
  setFile: (file: FileWithDataURL) => void;
  documentName: string;
  hint?: ReactNode;
};

const UploadFilePlaceholderRoot = styled.div<{ preview?: boolean }>`
  width: 100%;
  border: 2px solid var(--c-nd-600);
  border-radius: var(--double-unit);
  gap: var(--unit);
  color: var(--c-white);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  height: 220px;

  ${({ preview }) =>
    preview
      ? 'background-color: var(--c-black);'
      : 'border-style: dashed;  padding: var(--double-unit) 0;'}

  > img {
    max-width: 100%;
    max-height: 100%;
  }
`;

const maxFileSize = 7;
const Reupload = styled(LabelM)`
  text-decoration: underline;
  color: var(--c-nd-800);
  align-self: flex-start;
`;

const fileIsImageFn = (file: File | undefined) => {
  if (!file) return false;
  return file?.type.split('/')[0] === 'image';
};

export const FilePreview = ({ file }: { file: FileWithDataURL }) => {
  const fileIsImage = fileIsImageFn(file?.file);
  if (file.dataURL && fileIsImage) {
    return (
      <UploadFilePlaceholderRoot preview>
        <img alt="preview" src={file.dataURL} />
      </UploadFilePlaceholderRoot>
    );
  }
  return (
    <UploadFilePlaceholderRoot preview>
      <FontAwesomeIcon size="2xl" icon={iconFile} />
      <LabelM>{file.file.name}</LabelM>
    </UploadFilePlaceholderRoot>
  );
};

const UploadFilePlaceholder = ({
  icon,
  file,
  title,
  hint,
}: {
  icon: ReactNode;
  title: ReactNode;
  file: FileWithDataURL | undefined;
  hint?: ReactNode;
}) => {
  if (file) {
    return <FilePreview file={file} />;
  }
  return (
    <UploadFilePlaceholderRoot>
      {icon}
      <BodyM bold>{title}</BodyM>
      {!!hint && <LabelM color="var(--c-white)">{hint}</LabelM>}
      <LabelM color="var(--c-nd-600)">
        <FormattedMessage defaultMessage="JPEG, PNG or PDF only" />
      </LabelM>
      <LabelS color="var(--c-nd-600)">
        <FormattedMessage
          defaultMessage="(Max file size: {value}MB)"
          values={{
            value: maxFileSize,
          }}
        />
      </LabelS>
    </UploadFilePlaceholderRoot>
  );
};

const validExtensions = ['jpg', 'png', 'jpeg', 'pdf'];

export const UploadInput = ({
  name,
  file,
  setFile,
  icon,
  documentName,
  hint,
}: Props) => {
  return (
    <UploadFile
      name={name}
      currentFileUrl=""
      validExtensions={validExtensions}
      onChange={fileData => {
        setFile(fileData);
      }}
      type="image/png, image/jpeg, application/pdf"
      maxFileSizeMb={maxFileSize}
    >
      <UploadFilePlaceholder
        icon={<FontAwesomeIcon size="2xl" icon={icon} />}
        file={file}
        hint={hint}
        title={
          <FormattedMessage
            defaultMessage="Upload {documentName}"
            values={{ documentName }}
          />
        }
      />
      {!!file && (
        <Reupload>
          <FormattedMessage
            defaultMessage="Re-upload {documentName}"
            values={{ documentName }}
          />
        </Reupload>
      )}
    </UploadFile>
  );
};
